import React, { useCallback, useMemo, useState } from 'react';

import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import PasswordValidation from 'js/src/libs/password-validation';

type PasswordProps = {
    passwordVal: string;
    onChange: (e: TypeInputChangeEvent) => void;
    labelText?: string;
    'data-testid'?: string;
    formFeedbackMessage?: string;
};

/* TODO: add doc about this variation pattern */
const INPUT_ID = 'password';
const Password = React.memo(function Password({
    passwordVal,
    onChange,
    labelText = 'Password',
    'data-testid': dataTestid = 'password',
    formFeedbackMessage,
}: PasswordProps) {
    const [showingPassword, setShowingPassword] = useState(false);

    const togglePasswordVisibility = useCallback(() => {
        setShowingPassword(!showingPassword);
    }, [showingPassword]);

    const iconRight = useMemo(() => {
        return (
            <i
                className="material-symbols-outlined cursor-pointer"
                onClick={togglePasswordVisibility}
            >
                {showingPassword ? 'visibility_off' : 'visibility'}
            </i>
        );
    }, [showingPassword, togglePasswordVisibility]);

    return (
        <TextInput
            variant={TEXT_INPUT_VARIANTS.STANDARD}
            type={showingPassword ? 'text' : 'password'}
            id={INPUT_ID}
            data-testid={dataTestid}
            text={passwordVal}
            onChange={onChange}
            labelText={labelText}
            formFeedbackMessage={formFeedbackMessage}
            autocomplete={AUTOCOMPLETE_VALUES.CURRENT_PASSWORD}
            iconRight={iconRight}
        />
    );
});

type PasswordWithForgotLinkProps = {
    passwordVal: string;
    onChange: (e: TypeInputChangeEvent) => void;
    forgotPasswordUrl: string;
    formFeedbackMessage?: string;
};

export const PasswordWithForgotLink = React.memo(function PasswordWithForgotLink({
    passwordVal,
    onChange,
    forgotPasswordUrl,
    formFeedbackMessage,
}: PasswordWithForgotLinkProps) {
    return (
        <>
            <Password
                passwordVal={passwordVal}
                onChange={onChange}
                formFeedbackMessage={formFeedbackMessage}
            />
            <a
                href={forgotPasswordUrl}
                className="color-grey-500 text-decoration-underline ms-l5-2"
                data-testid="forgot-password-link"
            >
                Forgot password?
            </a>
        </>
    );
});

type PasswordCriteriaProps = {
    passwordVal: string;
};

export const PasswordCriteria = React.memo(function PasswordCriteria({
    passwordVal,
}: PasswordCriteriaProps) {
    const lowerCaseCondition = useMemo(
        () => PasswordValidation.lowercaseCondition(passwordVal),
        [passwordVal],
    );
    const upperCaseCondition = useMemo(
        () => PasswordValidation.uppercaseCondition(passwordVal),
        [passwordVal],
    );
    const characterMinCondition = useMemo(
        () => PasswordValidation.characterMinimumCondition(passwordVal),
        [passwordVal],
    );
    const numberSymbolCondition = useMemo(
        () => PasswordValidation.numberSymbolCondition(passwordVal),
        [passwordVal],
    );

    return (
        <div className="row mb-l5-16">
            <div className="col-md-6">
                <div
                    id="lowercase-condition"
                    className={`condition ${lowerCaseCondition ? 'condition-satisfied' : ''}`}
                >
                    <span>
                        <i className="material-symbols-outlined condition-icon material-symbols-f1">
                            circle
                        </i>
                        &nbsp;One lowercase character
                    </span>
                </div>
                <div
                    id="uppercase-condition"
                    className={`condition ${upperCaseCondition ? 'condition-satisfied' : ''}`}
                >
                    <span>
                        <i className="material-symbols-outlined condition-icon material-symbols-f1">
                            circle
                        </i>
                        &nbsp;One uppercase character
                    </span>
                </div>
            </div>
            <div className="col-md-6 no-margin-md">
                <div
                    id="character-minimum-condition"
                    className={`condition ${characterMinCondition ? 'condition-satisfied' : ''}`}
                >
                    <span>
                        <i className="material-symbols-outlined condition-icon material-symbols-f1">
                            circle
                        </i>
                        &nbsp;8 characters minimum
                    </span>
                </div>
                <div
                    id="number-symbol-condition"
                    className={`condition ${numberSymbolCondition ? 'condition-satisfied' : ''}`}
                >
                    <span>
                        <i className="material-symbols-outlined condition-icon material-symbols-f1">
                            circle
                        </i>
                        &nbsp;Contains a number or symbol
                    </span>
                </div>
            </div>
        </div>
    );
});

type PasswordWithCriteriaProps = {
    passwordVal: string;
    onChange: (e: TypeInputChangeEvent) => void;
    labelText?: string;
    formFeedbackMessage?: string;
};

export const PasswordWithCriteria = React.memo(function PasswordWithCriteria({
    passwordVal,
    onChange,
    labelText = 'Password',
    formFeedbackMessage,
}: PasswordWithCriteriaProps) {
    return (
        <>
            <Password
                passwordVal={passwordVal}
                onChange={onChange}
                labelText={labelText}
                formFeedbackMessage={formFeedbackMessage}
            />
            <br />
            <PasswordCriteria passwordVal={passwordVal} />
        </>
    );
});

export default Password;
